import { useState, useEffect } from 'react';
import { ArrowLeft, Edit2, Save, X } from 'lucide-react';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getAvatarUrl } from '../../utils/avatarUtils';



export default function AccountSettings() {
  const { uploadAvatar } = useAuth();
  const [userData, setUserData] = useState({
    full_name: '',
    first_name: '',
    last_name: '',
    user_name: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    role: '',
    avatar: null,
    profilePicture: null
  });

  // State to track which fields are being edited
  const [editingField, setEditingField] = useState(null);
  
  // State to store temporarily edited values
  const [editedValues, setEditedValues] = useState({});

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch user profile on component mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAPI.getUserProfile();
      if (response.success) {
        const avatarUrl = response.data?.avatar || response.data?.avatar_url;
        const fullName = response.data?.full_name || `${response.data?.first_name || ''} ${response.data?.last_name || ''}`.trim();
        const profilePicture = getAvatarUrl(
          avatarUrl,
          fullName,
          response.data?.first_name || '',
          response.data?.last_name || '',
          200
        );
        setUserData({
          ...response.data,
          avatar: avatarUrl,
          profilePicture: profilePicture
        });
      } else {
        setError(response.message || 'Failed to fetch profile');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to fetch profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    try {
      setSaving(true);
      const result = await uploadAvatar(file);
      if (result.success) {
        showSuccessAlertMessage('Profile picture updated successfully');
        // Reload profile to get updated avatar URL
        await fetchUserProfile();
        // Force page reload to update avatar in topnav
        window.location.reload();
      } else {
        alert(result.message || 'Failed to upload avatar');
      }
    } catch (err) {
      console.error('Error uploading avatar:', err);
      alert('Failed to upload avatar. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedValues({
      ...editedValues,
      [name]: value
    });
  };

  // Start editing a field
  const startEditing = (field) => {
    setEditingField(field);
    setEditedValues({
      ...editedValues,
      [field]: userData[field]
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingField(null);
  };

  // Save edited field
  const saveField = async (field) => {
    try {
      setSaving(true);
      const response = await authAPI.updateUserProfile(field, editedValues[field]);
      
      if (response.success) {
        setUserData({
          ...userData,
          [field]: editedValues[field]
        });
        setEditingField(null);
        // Show success message
        showSuccessAlertMessage(`${field.replace('_', ' ')} updated successfully`);
      } else {
        alert(response.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const showSuccessAlertMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => {
      setShowSuccessAlert(false);
      setSuccessMessage('');
    }, 4000);
  };

  // Render field based on edit state
  const renderField = (label, field) => {
    const isEditing = editingField === field;
    
    return (
      <div className="account-field-apss">
        <div className="field-label-apss">{label}</div>
        <div className="field-value-container-apss">
          {isEditing ? (
            <div className="editing-container-apss">
              <input
                type="text"
                name={field}
                value={editedValues[field] || ''}
                onChange={handleInputChange}
                className="field-input-apss"
              />
              <div className="edit-actions-apss">
                <button 
                  className="edit-action-button-apss save-button-apss"
                  onClick={() => saveField(field)}
                >
                  <Save size={16} />
                </button>
                <button 
                  className="edit-action-button-apss cancel-button-apss"
                  onClick={cancelEditing}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="field-value-apss">{userData[field]}</div>
              <button 
                className="edit-field-button-apss"
                onClick={() => startEditing(field)}
              >
                <Edit2 size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };


  const renderBioField = () => {
    const isEditing = editingField === 'bio';
    
    return (
      <div className="account-field-apss bio-field-apss">
        <div className="field-label-apss">Bio</div>
        <div className="field-value-container-apss">
          {isEditing ? (
            <div className="editing-container-apss">
              <textarea
                name="bio"
                value={editedValues.bio || ''}
                onChange={handleInputChange}
                className="field-textarea-apss"
                rows={4}
              />
              <div className="edit-actions-apss">
                <button 
                  className="edit-action-button-apss save-button-apss"
                  onClick={() => saveField('bio')}
                >
                  <Save size={16} />
                </button>
                <button 
                  className="edit-action-button-apss cancel-button-apss"
                  onClick={cancelEditing}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="field-value-apss bio-value-apss">{userData.bio}</div>
              <button 
                className="edit-field-button-apss"
                onClick={() => startEditing('bio')}
              >
                <Edit2 size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="account-settings-container-apss">
      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="success-alert-apss">
          <div className="success-alert-content-apss">
            <div className="success-alert-icon-apss">✓</div>
            <div className="success-alert-message-apss">{successMessage}</div>
            <button 
              className="success-alert-close-apss"
              onClick={() => setShowSuccessAlert(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className="account-settings-header-apss">
        <button className="back-button-apss" onClick={() => window.history.back()}>
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>
        <h1 className="account-settings-title-apss">Account Settings</h1>
      </div>

      
      <div className="profile-section-apss">
        <div className="profile-picture-container-apss">
          <img 
            src={userData.profilePicture || getAvatarUrl(
              null,
              userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
              userData.first_name || '',
              userData.last_name || '',
              200
            )} 
            alt="Profile" 
            className="profile-picture-apss" 
          />
          <label htmlFor="avatar-upload" className="change-picture-button-apss" style={{ cursor: 'pointer' }}>
            Change Photo
          </label>
          <input
            type="file"
            id="avatar-upload"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarUpload}
            disabled={saving}
          />
        </div>
        <div className="profile-name-role-apss">
          <h2 className="profile-name-apss">{userData.full_name || 'Loading...'}</h2>
          <div className="profile-role-apss">{userData.role || 'Loading...'}</div>
        </div>
      </div>

     
      <div className="settings-section-apss">
        <h3 className="section-title-apss">Personal Information</h3>
        <div className="section-content-apss">
          {loading ? (
            <div className="loading-profile-apss">
              <p>Loading profile information...</p>
            </div>
          ) : error ? (
            <div className="error-profile-apss">
              <p>{error}</p>
              <button onClick={fetchUserProfile} className="retry-button-apss">Retry</button>
            </div>
          ) : (
            <>
              {renderField('First Name', 'first_name')}
              {renderField('Last Name', 'last_name')}
              {renderField('Username', 'user_name')}
              {renderField('Email Address', 'email')}
              {renderField('Phone Number', 'phone')}
              {renderField('Location', 'location')}
              {renderBioField()}
            </>
          )}
        </div>
      </div>

     
    

    
      <div className="save-changes-container-apss">
        <button 
          className="save-all-changes-button-apss"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      <style jsx>{`
        .loading-profile-apss, .error-profile-apss {
          text-align: center;
          padding: 20px;
          color: #6b7280;
        }

        .error-profile-apss {
          color: #ef4444;
        }

        .retry-button-apss {
          background: #0e4410;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          margin-top: 10px;
        }

        .retry-button-apss:hover {
          background: #0a350c;
        }

        .save-all-changes-button-apss:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .edit-action-button-apss:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Success Alert Styles */
        .success-alert-apss {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1001;
          animation: slideInRight 0.3s ease-out;
        }

        .success-alert-content-apss {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          min-width: 300px;
        }

        .success-alert-icon-apss {
          background: #22c55e;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          flex-shrink: 0;
        }

        .success-alert-message-apss {
          color: #166534;
          font-weight: 500;
          flex: 1;
        }

        .success-alert-close-apss {
          background: none;
          border: none;
          color: #166534;
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .success-alert-close-apss:hover {
          color: #0f5132;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}